import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Collapse,
  Divider,
  Chip,
  Stack,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ListAlt as ListAltIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import CreateChecklist from "./CreateChecklistModal";
import EditChecklistModal from "./EditChecklistModal";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import queryClient from "../../../base/QueryClient";
import { enqueueSnackbar } from "notistack";
import {
  deleteChecklistByID,
  getAllChecklist,
} from "../../../api/services/settings";

const InstructionItem = ({
  instruction,
  level = 0,
  index = 0,
  parentIndex = "",
}) => {
  const serial = parentIndex ? `${parentIndex}.${index + 1}` : `${index + 1}`;

  return (
    <Box
      sx={{
        py: 1.25,
        pl: level > 0 ? 4 : 0,
        ml: level > 0 ? 2 : 0,
        borderBottom: level === 0 ? "1px solid #F2F4F7" : "none",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            minWidth: 28,
            color: "#94A3B8",
            fontSize: "0.78rem",
          }}
        >
          {serial}.
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#1A1A2E", fontWeight: 500, fontSize: "0.82rem" }}
        >
          {instruction.text}
        </Typography>
        {instruction.is_required && (
          <Chip
            label="Required"
            size="small"
            sx={{
              height: 18,
              fontSize: "0.6rem",
              fontWeight: 600,
              bgcolor: alpha("#EF4444", 0.08),
              color: "#EF4444",
            }}
          />
        )}
      </Box>

      {instruction.sub_instructions?.map((sub, idx) => (
        <InstructionItem
          key={idx}
          instruction={sub}
          level={level + 1}
          index={idx}
          parentIndex={serial}
        />
      ))}
    </Box>
  );
};

const VisitChecklist = () => {
  const [modal, setModal] = useState({
    create: false,
    edit: false,
    confirm: false,
  });
  const [expandedId, setExpandedId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const {
    data: checklists,
    refetch,
    isLoading,
  } = useQuery([QK.checklist], getAllChecklist);

  const { mutate: deleteChecklist } = useMutation(deleteChecklistByID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.checklist]);
      enqueueSnackbar("Checklist deleted", { variant: "success" });
      refetch();
    },
    onError: () => enqueueSnackbar("Delete failed", { variant: "error" }),
  });

  const handleDelete = () => {
    deleteChecklist(selectedId);
    setModal({ ...modal, confirm: false });
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const totalCount = checklists?.length || 0;

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ListAltIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Visit Checklists
              </Typography>
              <Typography
                variant="caption"
                color="#64748b"
                sx={{ fontSize: "0.7rem" }}
              >
                Manage care instructions
              </Typography>
            </Box>
            <Chip
              label={`${totalCount} item${totalCount !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
              }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setModal({ ...modal, create: true })}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            Add Checklist
          </Button>
        </Box>

        <Box sx={{ p: 2.5 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : checklists?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  bgcolor: alpha("#94A3B8", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <ListAltIcon sx={{ fontSize: 24, color: "#94A3B8" }} />
              </Box>
              <Typography
                variant="body2"
                color="#475467"
                fontWeight={500}
                sx={{ fontSize: "0.85rem" }}
              >
                No checklists available
              </Typography>
              <Typography
                variant="caption"
                color="#94A3B8"
                sx={{ fontSize: "0.7rem" }}
              >
                Click "Add Checklist" to create one
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {checklists.map((checklist) => (
                <Paper
                  key={checklist.id}
                  variant="outlined"
                  sx={{
                    borderRadius: "8px",
                    borderColor: "#E2E8F0",
                    overflow: "hidden",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: alpha("#2469F0", 0.4),
                      boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.08)",
                      "& .action-icons": { opacity: 1 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      "&:hover": { bgcolor: alpha("#2469F0", 0.02) },
                    }}
                    onClick={() => toggleExpand(checklist.id)}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "6px",
                          bgcolor: alpha("#2469F0", 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <ListAltIcon sx={{ fontSize: 16, color: "#2469F0" }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#1A1A2E"
                          sx={{ fontSize: "0.82rem" }}
                        >
                          {checklist.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="#94A3B8"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {checklist.instructions?.length || 0} items
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Box
                        className="action-icons"
                        sx={{
                          display: "flex",
                          gap: 0.25,
                          opacity: { xs: 1, md: 0.7 },
                          transition: "opacity 0.15s ease",
                        }}
                      >
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(checklist.id);
                              setModal({ ...modal, edit: true });
                            }}
                            sx={{
                              color: "#667085",
                              p: 0.5,
                              "&:hover": {
                                color: "#F59E0B",
                                bgcolor: alpha("#F59E0B", 0.06),
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(checklist.id);
                              setModal({ ...modal, confirm: true });
                            }}
                            sx={{
                              color: "#667085",
                              p: 0.5,
                              "&:hover": {
                                color: "#EF4444",
                                bgcolor: alpha("#EF4444", 0.06),
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <IconButton size="small" sx={{ color: "#94A3B8", p: 0.5 }}>
                        {expandedId === checklist.id ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Stack>
                  </Box>

                  <Collapse in={expandedId === checklist.id}>
                    <Divider sx={{ borderColor: "#E2E8F0" }} />
                    <Box sx={{ p: 2, bgcolor: "#F8FAFC" }}>
                      <Box
                        sx={{
                          bgcolor: "#FFFFFF",
                          borderRadius: "8px",
                          p: 2,
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        {checklist.instructions?.length ? (
                          checklist.instructions.map((inst, idx) => (
                            <InstructionItem
                              key={idx}
                              instruction={inst}
                              index={idx}
                            />
                          ))
                        ) : (
                          <Typography
                            variant="body2"
                            color="#94A3B8"
                            textAlign="center"
                            sx={{ py: 2, fontSize: "0.8rem" }}
                          >
                            No instructions
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>

      <CreateChecklist
        show={modal.create}
        handleClose={() => setModal({ ...modal, create: false })}
        refetch={refetch}
      />
      <EditChecklistModal
        show={modal.edit}
        handleClose={() => setModal({ ...modal, edit: false })}
        selectedChecklist={selectedId}
        refetch={refetch}
      />
      <ConfirmModal
        show={modal.confirm}
        handleClose={() => setModal({ ...modal, confirm: false })}
        handleConfirm={handleDelete}
        title="Delete Checklist"
        body="Are you sure? This action cannot be undone."
      />
    </Box>
  );
};

export default VisitChecklist;